// ============================================
// Path: /frontend/src/pages/ResetPasswordPage.jsx
// Purpose: Password reset page with token validation
// CSS OPTIMIZED: Mobile-first responsive design
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/useToast';
import { KeyRound, Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';
import { authAPI } from '../api/auth.api';

export default function ResetPasswordPage() {
  console.log('🎨 [ResetPasswordPage] Component mounted');

  // ============================================
  // SECTION: STATE MANAGEMENT
  // ============================================
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // ============================================
  // SECTION: TOKEN VALIDATION
  // ============================================
  useEffect(() => {
    if (!token) {
      console.log('❌ [ResetPasswordPage] No token found in URL');
      toast({
        variant: 'destructive',
        title: 'Invalid Reset Link',
        description: 'This password reset link is invalid or has expired.',
      });
      setTimeout(() => {
        console.log('🔄 [ResetPasswordPage] Redirecting to login...');
        navigate('/login');
      }, 3000);
    } else {
      console.log('✅ [ResetPasswordPage] Token found:', token.substring(0, 10) + '...');
    }
  }, [token, navigate, toast]);

  // ============================================
  // SECTION: PASSWORD VALIDATION
  // ============================================
  const validatePassword = () => {
    console.log('🔵 [ResetPasswordPage] Validating password...');

    if (newPassword.length < 8) {
      console.log('⚠️ [ResetPasswordPage] Password too short');
      toast({
        variant: 'destructive',
        title: 'Weak Password',
        description: 'Password must be at least 8 characters long.',
      });
      return false;
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      console.log('⚠️ [ResetPasswordPage] Password missing required characters');
      toast({
        variant: 'destructive',
        title: 'Weak Password',
        description: 'Password must contain uppercase, lowercase, and number.',
      });
      return false;
    }

    if (newPassword !== confirmPassword) {
      console.log('⚠️ [ResetPasswordPage] Passwords do not match');
      toast({
        variant: 'destructive',
        title: 'Passwords Don\'t Match',
        description: 'Please make sure both passwords match.',
      });
      return false;
    }

    console.log('✅ [ResetPasswordPage] Password validation passed');
    return true;
  };

  // ============================================
  // SECTION: FORM SUBMISSION
  // ============================================
  const handleResetPassword = async (e) => {
    e.preventDefault();
    console.log('👤 [ResetPasswordPage] Form submitted');

    if (!validatePassword()) return;

    setLoading(true);
    console.log('🔵 [ResetPasswordPage] Submitting password reset to API...');

    try {
      await authAPI.resetPassword(token, newPassword);
      
      console.log('✅ [ResetPasswordPage] Password reset successful');
      setSuccess(true);
      
      toast({
        title: 'Password Reset Successful!',
        description: 'Your password has been updated. Redirecting to login...',
      });

      setTimeout(() => {
        console.log('🔄 [ResetPasswordPage] Redirecting to login page');
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('❌ [ResetPasswordPage] Password reset error:', error);
      
      const errorMessage = error.response?.data?.message || 'Invalid or expired reset token. Please request a new one.';
      
      toast({
        variant: 'destructive',
        title: 'Reset Failed',
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // SECTION: CONDITIONAL RENDERING
  // ============================================
  
  // Show nothing if no token (redirect happening)
  if (!token) {
    console.log('⏳ [ResetPasswordPage] No token - returning null');
    return null;
  }

  // Show success state
  if (success) {
    console.log('🎨 [ResetPasswordPage] Rendering success state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-4 sm:pt-6 text-center space-y-3 sm:space-y-4">
            <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-green-600">Password Reset Successful!</h2>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
              Your password has been updated. Redirecting you to login...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show reset form
  console.log('🎨 [ResetPasswordPage] Rendering reset form');
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4">
      <Card className="w-full max-w-md mx-4">
        {/* CSS FIX 1: CardHeader responsive padding */}
        <CardHeader className="text-center space-y-1 sm:space-y-2 p-3 sm:p-4 md:p-6">
          {/* CSS FIX 2: CardTitle responsive sizing */}
          <CardTitle className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">
            Reset Your Password
          </CardTitle>
          {/* CSS FIX 3: Description text sizing */}
          <CardDescription className="text-xs sm:text-sm md:text-base">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        {/* CSS FIX 4: CardContent responsive padding */}
        <CardContent className="p-3 sm:p-4 md:p-6">
          {/* CSS FIX 5: Form spacing */}
          <form onSubmit={handleResetPassword} className="space-y-3 sm:space-y-4">
            {/* New Password Field */}
            <div className="space-y-1.5 sm:space-y-2">
              {/* CSS FIX 6: Label sizing */}
              <Label htmlFor="new-password" className="text-xs sm:text-sm">New Password</Label>
              <div className="relative">
                {/* CSS FIX 7: Icon sizing */}
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                {/* CSS FIX 8: Input height and text size */}
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  required
                  minLength={8}
                  className="pl-9 sm:pl-10 pr-10 h-9 sm:h-10 text-xs sm:text-sm"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>
              </div>
              {/* CSS FIX 9: Helper text sizing */}
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                At least 8 characters with uppercase, lowercase, and number
              </p>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="confirm-password" className="text-xs sm:text-sm">Confirm New Password</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  className="pl-9 sm:pl-10 pr-10 h-9 sm:h-10 text-xs sm:text-sm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* CSS FIX 10: Submit button margin and sizing */}
            <Button 
              type="submit" 
              className="w-full mt-4 sm:mt-6 font-semibold text-xs sm:text-sm h-10 sm:h-11" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          {/* CSS FIX 11: Footer text sizing and margin */}
          <div className="mt-3 sm:mt-4 text-center text-[10px] sm:text-xs md:text-sm">
            Remember your password?{' '}
            <Link 
              to="/login" 
              className="font-medium text-primary hover:underline transition-colors"
            >
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}